/**
 * Paso2ModificarEstructuras.jsx
 * ─────────────────────────────────────────────────────────────
 * Paso 2: edición condicional según los tipos de cambio seleccionados.
 *
 * Reglas de visibilidad:
 *   CHANGE_IDS.UNIDAD    (2) → muestra selector de nueva unidad
 *   CHANGE_IDS.HORARIOS  (1)
 *   CHANGE_IDS.EXTENSION (3) → muestran campos de horario en cada EstructuraEditor
 *   CHANGE_IDS.REDUCCION (4)    (extensión/reducción TAMBIÉN habilita horarios)
 *   CHANGE_IDS.PARADAS   (5) → muestra sección informativa de paradas
 *   Extensión / Reducción    → muestra banner de aviso sobre el redirect al trazo
 *
 * Props: { state, dispatch, catalogs }
 *   catalogs.rutas.data    → para resolver la ruta y estructuras
 *   catalogs.tiposServicio → dropdown de tipo de servicio
 *   catalogs.tiposVehicle  → tarjetas de tipo de vehículo
 */

import { memo, useCallback } from "react";
import { InputText } from "primereact/inputtext";
import { Tag       } from "primereact/tag";

import {
    getMod,
    CmrCardTitle,
    CmrDaySelector,
    CmrDropdownField,
    CmrDropdownSkeleton,
    CmrFieldError,
    CmrFieldLabel,
    CmrHelpText,
    CmrInfoBanner,
    CmrInfoRow,
} from "../cmrShared";

import {
    CHANGE_IDS,
    hasChange, hasFrecuencia,
    hasTrazoCambio,
    showHorarios as shouldShowHorarios,
    frecuenciaModifica,
    frecuenciaGeneraNueva,
    getChangeLabels,
    formatList,
    getIdsPorAccion
} from "../hooks/ChangeTypes";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";

// ─── ÁTOMO: tarjeta de vehículo ───────────────────────────────────────────────

const VehicleCard = memo(({ vehiculo, seleccionado, esActual, onSelect }) => (
    <div
        role="radio"
        aria-checked={seleccionado}
        tabIndex={0}
        className={`cmr-vehicle-card flex align-items-center gap-3 p-3${seleccionado ? " cmr-vehicle-card--selected" : ""}`}
        onClick={() => onSelect(vehiculo.ID)}
        onKeyDown={(e) => e.key === " " && onSelect(vehiculo.ID)}
    >
        <div
            className={`cmr-checkbox-box flex align-items-center justify-content-center${seleccionado ? " cmr-checkbox-box--checked" : ""}`}
            aria-hidden="true"
        >
            {seleccionado && <i className="pi pi-check" style={{ fontSize: "0.75rem" }} />}
        </div>
        <i className="pi pi-truck" aria-hidden="true" />
        <div>
            <div className="font-bold text-color flex align-items-center gap-2">
                {vehiculo.TIPO}
                {esActual && <Tag value="Actual" severity="success" />}
            </div>
            <div className="text-sm text-color-secondary">{vehiculo.CAPACIDAD_PASAJEROS} pasajeros</div>
        </div>
    </div>
));
VehicleCard.displayName = "VehicleCard";

// ─── MOLÉCULA: editor de una estructura ──────────────────────────────────────

/**
 * Renderiza los campos editables de una estructura.
 * Los campos de horario solo se muestran cuando `showHorarioFields` es true.
 * El turno solo es editable con cambio de frecuencia (`showTurno`).
 */
const EstructuraEditor = memo(({ estructura, idx, modificaciones, dispatch, tiposServicio, tiposTurno, showHorarioFields, showTurno }) => {
    const update = useCallback(
        (field, value) => dispatch({ type: "UPDATE_MODIFICACION", estructuraId: estructura.id, field, value }),
        [dispatch, estructura.id]
    );

    const nombre       = getMod(modificaciones, estructura.id, "nombre",       estructura.nombre);
    const turno        = getMod(modificaciones, estructura.id, "turno",        estructura.turno ?? null);
    const tipoServicio = getMod(modificaciones, estructura.id, "tipoServicio", estructura.tipoServicio);
    const horaInicio   = getMod(modificaciones, estructura.id, "horaInicio",   estructura.horaInicio);
    const horaFin      = getMod(modificaciones, estructura.id, "horaFin",      estructura.horaFin);
    const dias         = getMod(modificaciones, estructura.id, "dias",         estructura.dias);

    // Entrada/salida depende del turno elegido; si el catálogo aún no carga, se usa el original
    const turnoSel  = (Array.isArray(tiposTurno?.data) ? tiposTurno.data : []).find((t) => t.id === turno);
    const isEntrada = Boolean(turnoSel ? turnoSel.entrance : estructura?.entrada);

    return (
        <div className="cmr-edit-card">
            {/* Header del turno */}
            <div className="cmr-edit-card__header flex align-items-center gap-3 p-3">
                <div
                    className="cmr-edit-card__header-number flex align-items-center justify-content-center flex-shrink-0"
                    aria-hidden="true"
                >
                    {idx + 1}
                </div>
                <div>
                    <p className="cmr-edit-card__header-title m-0">{estructura.nombre}</p>
                    <p className="cmr-edit-card__header-sub m-0">
                        {showHorarioFields
                            ? "Modifica los datos de este turno"
                            : "Turno incluido en la modificación"}
                    </p>
                </div>
            </div>

            <div className="p-4">
                <div className="grid">
                    {/* Nombre (siempre visible, siempre deshabilitado) */}
                    <div className="col-12 md:col-6 cmr-field-group">
                        <CmrFieldLabel required tooltip="Nombre identificador del turno">
                            Nombre del turno
                        </CmrFieldLabel>
                        <InputText
                            value={nombre}
                            onChange={(e) => update("nombre", e.target.value)}
                            placeholder="Ej. ENTRADA T1"
                            className="w-full"
                            aria-required="true"
                            disabled
                        />
                    </div>

                    {/* Turno — solo con cambio de frecuencia */}
                    {showTurno && (
                        <div className="col-12 md:col-6">
                            <CmrDropdownField
                                label="Turno"
                                required
                                tooltip="Seleccione un turno"
                                catalog={tiposTurno}
                                value={turno}
                                onChange={(e) => update("turno", e.value)}
                            />
                        </div>
                    )}

                    {/* Tipo de servicio — solo con horarios */}
                    {showHorarioFields && (
                        <div className="col-12 md:col-6">
                            <CmrDropdownField
                                label="Tipo de servicio"
                                required
                                tooltip="Tipo de servicio para este turno"
                                catalog={tiposServicio}
                                value={tipoServicio}
                                onChange={(e) => update("tipoServicio", e.value)}
                            />
                        </div>
                    )}
                </div>

                {/* Horarios — solo con horarios */}
                {showHorarioFields && (
                    <div className="grid">
                        <div className="col-12 md:col-6 cmr-field-group">
                            <CmrFieldLabel
                                required
                                tooltip={
                                    isEntrada
                                        ? "Hora a la que inicia el turno"
                                        : "Hora en que la unidad sale de planta"
                                }
                            >
                                {isEntrada ? "Hora de inicio de turno" : "Hora inicio en planta"}
                            </CmrFieldLabel>

                            <InputText
                                type="time"
                                value={horaInicio}
                                onChange={(e) => update("horaInicio", e.target.value)}
                                disabled={isEntrada}
                                className="w-full"
                                aria-required="true"
                            />

                            <CmrHelpText icon="pi-clock">
                                {isEntrada
                                    ? "Indica la hora en que inicia el turno. Este valor se actualizará automáticamente al modificar el horario de las paradas"
                                    : "Indica la hora en que la unidad sale de planta"}
                            </CmrHelpText>
                        </div>

                        <div className="col-12 md:col-6 cmr-field-group">
                            <CmrFieldLabel
                                required
                                tooltip={isEntrada
                                    ? "Hora en que la unidad llega a planta"
                                    : "Hora en que finaliza el turno"}
                            >
                                {isEntrada ? "Hora llegada a planta" : "Hora fin de turno"}
                            </CmrFieldLabel>

                            <InputText
                                type="time"
                                value={horaFin}
                                onChange={(e) => update("horaFin", e.target.value)}
                                disabled={!isEntrada}
                                className="w-full"
                                aria-required="true"
                            />

                            <CmrHelpText icon="pi-clock">
                                {isEntrada
                                    ? "Indica la hora en que la unidad llega a planta"
                                    : "Indica la hora en que finaliza el turno. Este valor se actualizará automáticamente al modificar el horario de las paradas."}
                            </CmrHelpText>
                        </div>
                    </div>
                )}

                {/* Días — solo con horarios */}
                {showHorarioFields && (
                    <div className="cmr-field-group">
                        <CmrFieldLabel required tooltip="Días en que opera este turno">
                            Días de operación
                        </CmrFieldLabel>
                        <CmrDaySelector selected={dias} onChange={(val) => update("dias", val)} />
                        <CmrHelpText icon="pi-calendar">
                            Selecciona los días en que operará este turno
                        </CmrHelpText>
                    </div>
                )}

                {/* Valores originales de referencia */}
                {showHorarioFields && (
                    <CmrInfoBanner severity="info">
                        <strong>Valores originales:</strong>{" "}
                        {estructura.horaInicio} – {estructura.horaFin} · {estructura.dias.join(", ")} · {estructura.tipoServicio}
                    </CmrInfoBanner>
                )}
            </div>
        </div>
    );
});
EstructuraEditor.displayName = "EstructuraEditor";

// ─── PASO 2 ───────────────────────────────────────────────────────────────────

const Paso2ModificarEstructuras = memo(({ state, dispatch, catalogs }) => {
    const {
        rutaId, changeType, nuevaUnidad,
        modificaciones, frecuenciaModo, estructurasCopiadas
    } = state;
    const { tiposServicio, tiposTurno, tiposVehicle, rutas, recomendacionUnidad } = catalogs;

    // ── Flags de visibilidad por tipo de cambio ───────────────────────────────
    const showVehicle       = hasChange(changeType, CHANGE_IDS.UNIDAD);
    const showHorarioFields = shouldShowHorarios(changeType);
    const showParadas       = hasChange(changeType, CHANGE_IDS.PARADAS);
    const showTrazoBanner   = hasTrazoCambio(changeType);
    const showFrecuencia    = hasFrecuencia(changeType);

    const rec = recomendacionUnidad.data?.[0] ?? null;

    // Flags de sub-modo de frecuencia
    const frecModifica    = showFrecuencia && frecuenciaModifica(frecuenciaModo);
    const frecGeneraNueva = showFrecuencia && frecuenciaGeneraNueva(frecuenciaModo);

    // ── Datos de la ruta ──────────────────────────────────────────────────────
    const rutaSeleccionada = rutas.data.find((r) => r.id === rutaId);

    const { idsAEditar, idsParaCopiar } = getIdsPorAccion(state);

    const estructurasParaCopiar = rutaSeleccionada?.estructuras.filter(
        (e) => idsParaCopiar.includes(e.id)
    ) ?? [];

    const cambios = getChangeLabels(changeType);

    const handleSelectVehiculo = useCallback(
        (valor) => dispatch({ type: "SET_NUEVA_UNIDAD", payload: nuevaUnidad === valor ? "" : valor }),
        [dispatch, nuevaUnidad]
    );

    const estructurasAEditar = rutaSeleccionada?.estructuras.filter((e) => idsAEditar.includes(e.id)) ?? [];

    return (
        <>
            <CmrInfoBanner severity="info">
                <strong>Paso 2: Modifica los cambios seleccionados.</strong>
                <span className="ml-1">
                    Solo se muestran las secciones correspondientes a los tipos de cambio elegidos.
                </span>
            </CmrInfoBanner>

            {/* ── Aviso de redirección al editor de trazo ───────────────── */}
            {(hasTrazoCambio(changeType) || hasChange(changeType, CHANGE_IDS.PARADAS)) && (
                <CmrInfoBanner severity="warn">
                    <strong>
                        {formatList(cambios)}:
                    </strong>
                    <span className="ml-1">
                        Al guardar, serás redirigido al <strong>editor de trazo</strong> para
                        definir el recorrido y las paradas en el mapa.
                        {showHorarioFields && " También podrás ajustar los horarios de las paradas."}
                    </span>
                </CmrInfoBanner>
            )}

            {/* ── Modificar paradas (informativo) ───────────────────────── */}
            {(showParadas && !showHorarioFields) && (
                <div className="cmr-card p-4">
                    <CmrCardTitle
                        icon="pi-map-marker"
                        title="Modificación de paradas"
                        subtitle="Las paradas se gestionan en el editor de trazo"
                    />
                    <div className="flex flex-column gap-2">
                        {estructurasAEditar.map((est) => (
                            <CmrInfoRow key={est.id}>
                                <i className="pi pi-clock" aria-hidden="true" />
                                <span className="font-bold">{est.nombre}</span>
                                <span className="text-color-secondary">{est.horaInicio} – {est.horaFin}</span>
                                <Tag value={est.dias.join(", ")} severity="info" style={{ fontSize: "0.75rem" }} />
                            </CmrInfoRow>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Cambio de unidad ──────────────────────────────────────── */}
            {showVehicle && (
                <div className="cmr-card p-4">
                    <CmrCardTitle
                        icon="pi-truck"
                        title="Cambio de unidad"
                        subtitle="Selecciona el nuevo tipo de vehículo para toda la ruta"
                    />

                    {/* ── Recomendación basada en últimos 30 días ── */}
                    {recomendacionUnidad.loading && (
                        <div className="flex align-items-center gap-2 mb-3">
                            <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" />
                            <span className="text-sm text-color-secondary">Analizando operación...</span>
                        </div>
                    )}


                    {rec && !recomendacionUnidad.loading && (
                        <CmrInfoBanner severity="info">
                            <div className="flex flex-column gap-1">
                                <span className="font-bold">Análisis últimos 30 días</span>
                                <div className="flex flex-wrap gap-3 mt-1 text-sm">
                                    <span><i className="pi pi-truck mr-1" />Unidad más usada: <strong>{rec.Unidad_Predominante}</strong></span>
                                    <span><i className="pi pi-users mr-1" />Ocupación máx: <strong>{rec.Ocupacion_Maxima} pax</strong></span>
                                    <span><i className="pi pi-chart-bar mr-1" />Uso: <strong>{rec.Uso_Maximo_Pct}</strong></span>
                                </div>
                                <div className="mt-1 text-sm">
                                    <i className="pi pi-lightbulb mr-1" />{rec.Recomendacion}
                                </div>
                            </div>
                        </CmrInfoBanner>
                    )}

                    <CmrInfoRow>
                        <i className="pi pi-map-marker" aria-hidden="true" />
                        <span>Unidad actual:</span>
                        <span className="font-bold text-color">{rutaSeleccionada?.unidadActual.nombre}</span>
                        <span className="text-color-secondary">({rutaSeleccionada?.unidadActual.capacidad})</span>
                    </CmrInfoRow>

                    <div className="mt-3 mb-2">
                        <CmrFieldLabel tooltip="Deja sin seleccionar para mantener la unidad actual">
                            Nueva unidad{" "}
                            <span className="text-color-secondary font-normal">(dejar vacío para no cambiar)</span>
                        </CmrFieldLabel>
                    </div>

                    {tiposVehicle.loading ? (
                        <CmrDropdownSkeleton label="Cargando tipos de vehículo..." />
                    ) : (
                        <div className="grid" role="radiogroup" aria-label="Selección de nueva unidad">
                            {(Array.isArray(tiposVehicle.data) ? tiposVehicle.data : []).map((v) => (
                                <div key={v.ID} className="col-12 md:col-6">
                                    <VehicleCard
                                        vehiculo={v}
                                        seleccionado={nuevaUnidad === v.ID}
                                        esActual={rutaSeleccionada?.unidadActual.ID === v.ID}
                                        onSelect={handleSelectVehiculo}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    <CmrFieldError message={tiposVehicle.error} />
                    <CmrHelpText icon="pi-info-circle">
                        El cambio de unidad aplica a todos los turnos de esta ruta
                    </CmrHelpText>
                </div>
            )}

            {/* ── Editores de estructura (horarios) ─────────────────────── */}
            {(showHorarioFields || frecModifica) && estructurasAEditar.map((est, idx) => (
                <EstructuraEditor
                    key={est.id}
                    estructura={est}
                    idx={idx}
                    modificaciones={modificaciones}
                    dispatch={dispatch}
                    tiposServicio={tiposServicio}
                    tiposTurno={tiposTurno}
                    showHorarioFields={showHorarioFields || frecModifica}
                    showTurno={frecModifica}
                />
            ))}

            {/* ── Frecuencia: Modificar existentes (banner informativo) ── */}
            {frecModifica && !showHorarioFields && estructurasAEditar.length > 0 && (
                <CmrInfoBanner severity="info">
                    <strong>Modo: Modificar estructura existente.</strong>{" "}
                    Los cambios que realices se aplicarán directamente a las estructuras originales.
                </CmrInfoBanner>
            )}

            {/* ── Frecuencia: Copias de estructuras (modo "nueva" o "ambas") ──
                 Solo se muestra si realmente hay (o va a haber) copias: en modo AMBAS,
                 si el usuario marcó todas las estructuras como "solo editar", no hay
                 nada que copiar y esta tarjeta no debe aparecer. */}
            {frecGeneraNueva && idsParaCopiar.length > 0 && (
                <div className="cmr-card p-4">
                    <CmrCardTitle
                        icon="pi-copy"
                        title="Nuevas estructuras (copias)"
                        subtitle="Estas son copias de las estructuras seleccionadas. Modifícalas sin afectar las originales."
                    />

                    {estructurasCopiadas.length === 0 && state.copiasIgnoradas.length === 0 && (
                        <CmrInfoBanner severity="warn">
                            No se han generado copias aún. Regresa al paso anterior y selecciona las estructuras.
                        </CmrInfoBanner>
                    )}

                    {estructurasCopiadas.length === 0 && state.copiasIgnoradas.length > 0 && (
                        <CmrInfoBanner severity="warn">
                            Eliminaste todas las copias. Para generar una nueva, vuelve al paso anterior y
                            marca de nuevo la estructura como "Generar copia" o "Ambas".
                        </CmrInfoBanner>
                    )}

                    <div className="flex flex-column gap-3">
                        {estructurasCopiadas.map((est, idx) => {
                            const upd = (field, value) =>
                                dispatch({ type: "UPDATE_ESTRUCTURA_COPIADA", payload: { _id: est._id, field, value } });

                            // Entrada/salida depende del turno que tenga la copia (puede cambiarse arriba);
                            // si el catálogo aún no carga, se usa el de la estructura origen.
                            const turnoCopia = (Array.isArray(tiposTurno.data) ? tiposTurno.data : [])
                                .find((t) => t.id === est.turno);
                            const isEntrada  = Boolean(turnoCopia ? turnoCopia.entrance : est.entrada);

                            return (
                                <div key={est._id} className="cmr-edit-card">
                                    {/* Header */}
                                    <div className="cmr-edit-card__header flex align-items-center justify-content-between gap-3 p-3">
                                        <div className="flex align-items-center gap-3">
                                            <div className="cmr-edit-card__header-number flex align-items-center justify-content-center flex-shrink-0">
                                                {idx + 1}
                                            </div>
                                            <div>
                                                <p className="cmr-edit-card__header-title m-0">
                                                    {est.nombre || "Copia de estructura"}
                                                </p>
                                                <p className="cmr-edit-card__header-sub m-0">
                                                    Copia generada — modifícala libremente
                                                </p>
                                            </div>
                                            <Tag value="Copia" severity="warning" />
                                        </div>
                                        <Button
                                            icon="pi pi-trash"
                                            className="text-white p-button-sm"
                                            tooltip="Eliminar esta copia"
                                            onClick={() => dispatch({ type: "REMOVE_ESTRUCTURA_COPIADA", payload: est._id })}
                                        />
                                    </div>

                                    {/* Campos */}
                                    <div className="p-4">
                                        <div className="grid">
                                            <div className="col-12 md:col-6 cmr-field-group">
                                                <CmrDropdownField
                                                    label="Turno"
                                                    required
                                                    tooltip="Seleccione un turno"
                                                    catalog={tiposTurno}
                                                    value={est.turno}
                                                    onChange={(e) => upd("turno", e.value)}
                                                />
                                            </div>

                                            <div className="col-12 md:col-6">
                                                <CmrDropdownField
                                                    label="Tipo de servicio"
                                                    required
                                                    tooltip="Tipo de servicio para este turno"
                                                    catalog={tiposServicio}
                                                    value={est.tipoServicio}
                                                    onChange={(e) => upd("tipoServicio", e.value)}
                                                />
                                            </div>

                                            <div className="col-12 md:col-6 cmr-field-group">
                                                <CmrFieldLabel >Hora de inicio</CmrFieldLabel>
                                                <InputText
                                                    type="time"
                                                    value={est.horaInicio}
                                                    onChange={(e) => upd("horaInicio", e.target.value)}
                                                    disabled={isEntrada}
                                                    className="w-full"
                                                />
                                                <CmrHelpText icon="pi-clock">Hora de inicio del servicio</CmrHelpText>
                                            </div>

                                            <div className="col-12 md:col-6 cmr-field-group">
                                                <CmrFieldLabel >Hora de fin</CmrFieldLabel>
                                                <InputText
                                                    type="time"
                                                    value={est.horaFin}
                                                    onChange={(e) => upd("horaFin", e.target.value)}
                                                    disabled={!isEntrada}
                                                    className="w-full"
                                                />
                                                <CmrHelpText icon="pi-clock">Hora de finalización del servicio</CmrHelpText>
                                            </div>
                                        </div>

                                        <div className="cmr-field-group">
                                            <CmrFieldLabel required>Días de operación</CmrFieldLabel>
                                            <CmrDaySelector
                                                selected={est.dias}
                                                onChange={(val) => upd("dias", val)}
                                            />
                                            <CmrHelpText icon="pi-calendar">
                                                Selecciona los días en que operará este turno
                                            </CmrHelpText>
                                        </div>

                                        <CmrInfoBanner severity="info">
                                            <strong>Estructura origen:</strong>{" "}
                                            {est.nombre.replace(" (Copia)", "")} — estos valores son independientes del original.
                                        </CmrInfoBanner>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Mensaje cuando solo hay cambio de unidad y no hay más secciones */}
            {!showVehicle && !showHorarioFields && !showParadas && !showTrazoBanner && (
                <CmrInfoBanner severity="info">
                    No hay campos adicionales que configurar para los tipos de cambio seleccionados.
                    Puedes continuar al paso de confirmación.
                </CmrInfoBanner>
            )}
        </>
    );
});

Paso2ModificarEstructuras.displayName = "Paso2ModificarEstructuras";
export default Paso2ModificarEstructuras;
